import { useMemo } from 'react';
import * as Adflow from 'adflow';
import { useParams } from 'react-router-dom';
import { APIClientContext as FigmaAPIClientContext } from '@adflow/figma';
import { APIClientContext as SelectAdvertiserAPIClientContext } from '@adflow/marketing-cloud';
import { APIClientContext as ShadowBrandAPIClientContext } from '@adflow/shadow-brand';
import { Datasources, EditorState } from '@adflow/types';
import { TRPCClient } from '../utils/apiClient';
import { useAdTemplateData } from '../utils/hooks/useAdTemplateData';
import { APIClientContext as CreatomateAPIContextProvider } from '@adflow/creatomate';
import { CustomAlert } from '../components/customAlert';
import TemplateBuilderPreview from '../components/TemplateBuilderPreview';
import { loadAdvertiserFonts } from '../webmcp/loadAdvertiserFonts';
import TemplateBuilderOperationAuditProvider from '../operationAudit/TemplateBuilderOperationAuditProvider';
import { withTemplateSaveMetadata } from '../save/withTemplateSaveMetadata';
import TemplateWebMCPHost from '../webmcp/TemplateWebMCPHost';
import { useEditorModules } from '../utils/hooks/useEditorModules';
import { gql } from '@apollo/client';
import apolloClient from '../../apolloClient';

const UPSERT_TEMPLATE_MUTATION = gql`
  mutation UpsertTemplate($templateId: String, $data: JSON!) {
    upsertTemplate(templateId: $templateId, data: $data) {
      id
    }
  }
`;

function Edit() {
  const { modules } = useEditorModules();
  const { id: idParam } = useParams<{ id: string | undefined }>();
  const id = idParam || '';

  const {
    adTemplate,
    brand,
    brandKit,
    fonts,
    ready,
    warningOpen,
    warningMessage,
    setWarningOpen,
    hasEditPermissions,
  } = useAdTemplateData(id);

  const defaultStoreValues = useMemo<Partial<EditorState>>(() => {
    if (!adTemplate) return {};

    let teamAssetsConfig = adTemplate.displayOptions?.teamAssets;
    if (!teamAssetsConfig) {
      teamAssetsConfig = {
        type: 'JERSEY',
        fallback: 'NONE',
      };
    }

    return {
      ...adTemplate,
      elements: adTemplate.adElements,
      brand,
      brandKit,
      brandKitId: adTemplate.brandKitId || null,
      customFonts: fonts,
      teamAssets: teamAssetsConfig,
      thumbnailURL: adTemplate.thumbnail || '', // Map thumbnail to thumbnailURL for editor
      animation: adTemplate.animation ?? null,
      persistedRevision: adTemplate.persistedRevision ?? 0,
    };
  }, [adTemplate, brand, brandKit, fonts]);

  const dataSources = useMemo(() => {
    return Datasources.createDataSourceDefinitions(adTemplate?.dataSources || null);
  }, [adTemplate]);

  const renderPreview = (args: Adflow.RenderPreviewArgs) => <TemplateBuilderPreview {...args} />;

  const onSave = async (editorState: EditorState) => {
    await apolloClient.mutate({
      mutation: UPSERT_TEMPLATE_MUTATION,
      variables: {
        templateId: id,
        data: withTemplateSaveMetadata({
          name: editorState.name,
          brandId: editorState.brand?._id,
          brandKitId: editorState.brandKitId,
          figmaComponentId: adTemplate?.figmaComponentId ?? null,
          dataSources: editorState.dataSources,
          adElements: editorState.elements,
          thumbnail: editorState.thumbnailURL,
          displayOptions: editorState.displayOptions,
          businessEntityId: editorState.businessEntityId,
          importSourceType: editorState.importSourceType,
          importSourceJSON: editorState.importSourceJSON,
          videoProjectId: editorState.videoProjectId,
          animation: editorState.animation,
          bgColour: editorState.bgColour,
        }),
      },
    });
  };

  if (!ready) {
    if (!hasEditPermissions) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'start',
            height: '100vh',
            width: '100%',
            padding: '4rem 2rem',
          }}
        >
          <CustomAlert
            type="error"
            title="Unauthorized"
            message="You do not have the permissions to access this page"
            closable={false}
          />
        </div>
      );
    }
    return <span>Loading</span>;
  }

  if (warningOpen) {
    return (
      <Adflow.WarningModal
        isOpen={warningOpen}
        onClose={() => setWarningOpen(false)}
        warning={warningMessage}
        title="Warning"
      ></Adflow.WarningModal>
    );
  }

  return (
    <div
      className="editor-page"
      style={{ display: 'flex', flexDirection: 'column', height: '100vh', minHeight: 0 }}
    >
      <Adflow.EditorProvider dataSources={dataSources} defaultStoreValues={defaultStoreValues}>
        <TemplateBuilderOperationAuditProvider templateId={id}>
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <FigmaAPIClientContext.Provider {...TRPCClient.figmaClient}>
              <ShadowBrandAPIClientContext.Provider {...TRPCClient.consoleClient}>
                <CreatomateAPIContextProvider.Provider {...TRPCClient.creatomateClient}>
                  <SelectAdvertiserAPIClientContext.Provider {...TRPCClient.marketingCloudClient}>
                    <TemplateWebMCPHost
                      templateId={id}
                      editorReady={ready}
                      hasEditPermissions={hasEditPermissions}
                      routeMode="edit"
                      listAdvertisers={TRPCClient.marketingCloudClient.listAdvertisers}
                      loadAdvertiserFonts={loadAdvertiserFonts}
                    />
                    <Adflow.Editor
                      saveButtonVisible={hasEditPermissions}
                      onSave={onSave}
                      modules={modules}
                      renderPreview={renderPreview}
                      isEditMode={true}
                    ></Adflow.Editor>
                  </SelectAdvertiserAPIClientContext.Provider>
                </CreatomateAPIContextProvider.Provider>
              </ShadowBrandAPIClientContext.Provider>
            </FigmaAPIClientContext.Provider>
          </div>
        </TemplateBuilderOperationAuditProvider>
      </Adflow.EditorProvider>
    </div>
  );
}
export default Edit;
